import {
  NativeTabsViewIOS,
  appendIconOptionsIOS,
  type NativeTabOptions,
  type NativeTabsTriggerIconProps,
} from "@-/com.npmjs.expo-router/native-tabs";
import {
  useRouteBodyState,
  useRouteBodyRef,
  RouteBodyStateScope,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { detailsTabBarIcon } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-floating-tab-bar";
import {
  createMaterialTopTabBarProps,
  DynamicIconsTabBar,
} from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { MorphemeInfoFrostedTabs } from "#morpheme-info-frosted-tabs";
import { useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { MorphemeInfoTabHostContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";
import { useCSSVariable, useResolveClassNames, useUniwind } from "@-/com.npmjs.uniwind";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { usePrimaryNavigationStripFloatingTabs } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

const infoTabIcons: Record<string, NativeTabsTriggerIconProps> = {
  caligraphy: { md: "brush", sf: "paintbrush.pointed" },
  characters: { md: "translate", sf: "character" },
  compounds: { md: "account_tree", sf: "square.stack.3d.up" },
  dictionary: { md: "menu_book", sf: "book" },
  mnemonics: { md: "psychology", sf: "brain.head.profile" },
  progress: { md: "bar_chart", sf: "chart.bar" },
  sentences: { md: "chat_bubble_outline", sf: "text.bubble" },
};

export interface MorphemeInfoTab {
  segment: string;
  title: string;
}

export interface MorphemeInfoViewProps {
  initialTabSegment?: string;
  morpheme: string;
  onActiveTabChange?: (segment: string) => void;
  screenRenderers: Record<string, (morpheme: string) => ReactElement>;
  stateKeyPrefix: string;
  /**
   * A glass circle to the right of the bottom tabs, given its diameter: the toolbar's circle
   * buttons' size, centred on the tab platter. On iOS 26+ it sits in the slot the native tab bar
   * reserves for it, on Android beside the frosted tabs.
   */
  tabBarAccessory?: (diameter: number) => ReactNode;
  tabList: readonly MorphemeInfoTab[];
}

interface ReferenceTabRoute {
  key: string;
  name: string;
  render: () => ReactElement;
  title: string;
}

function tabIndexForSegment(
  segment: string | undefined,
  tabList: readonly MorphemeInfoTab[],
): number {
  if (segment === undefined) {
    return 0;
  }
  const index = tabList.findIndex((tab) => tab.segment === segment);
  return index === -1 ? 0 : index;
}

export function MorphemeInfoView({
  initialTabSegment,
  morpheme,
  onActiveTabChange,
  screenRenderers,
  stateKeyPrefix,
  tabBarAccessory,
  tabList,
}: MorphemeInfoViewProps) {
  const { theme } = useUniwind();
  const masteryColor = String(useCSSVariable("--stage-mastered") ?? "#f97316");
  const backgroundStyle = useResolveClassNames("bg-background");
  const [nativeProvenance, setNativeProvenance] = useState(0);
  const [accessoryFrame, setAccessoryFrame] = useState<{
    height: number;
    x: number;
    y: number;
  } | null>(null);
  const tabHost = useContext(MorphemeInfoTabHostContext);
  // The accessory matches the header's circle buttons rather than the taller tab platter.
  const { diameter: accessoryDiameter } = useToolbarGridMetrics();
  const initialSegment = tabHost ? "dictionary" : initialTabSegment;
  const isHostActive = tabHost?.isActive ?? true;
  const [morphemeHrefObject] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.morphemeHrefObject,
  );
  const [activeIndex, setActiveIndex] = useRouteBodyState("info-section", () =>
    tabIndexForSegment(initialSegment, tabList),
  );

  const previousInitialSelection = useRouteBodyRef(
    "info-initial-selection",
    JSON.stringify([initialSegment, morpheme, isHostActive]),
  );
  // Reference pages follow deep links even while pre-mounted. The card's binder instead starts
  // at Dictionary each time Info opens, independently of the surrounding route's tab parameter.
  useEffect(() => {
    const signature = JSON.stringify([initialSegment, morpheme, isHostActive]);
    if (previousInitialSelection.current === signature) {
      return;
    }
    previousInitialSelection.current = signature;
    setActiveIndex(tabIndexForSegment(initialSegment, tabList));
  }, [initialSegment, morpheme, tabList, isHostActive, previousInitialSelection, setActiveIndex]);

  const tabRouteList = useMemo<ReferenceTabRoute[]>(
    () =>
      tabList.flatMap((tab) => {
        const renderer = screenRenderers[tab.segment];
        if (renderer === undefined) {
          return [];
        }
        return [
          {
            key: tab.segment + morpheme,
            name: tab.segment,
            render: () => renderer(morpheme),
            title: tab.title,
          },
        ];
      }),
    [morpheme, screenRenderers, tabList],
  );

  const activeRoute = tabRouteList[activeIndex] ?? tabRouteList[0];
  // On a reference page (not a card's binder) the tabs float just above the primary navigation
  // strip, where a bottom-placed Admin panel must not cover them.
  const isNavigationFocused = useIsNavigationFocused();
  usePrimaryNavigationStripFloatingTabs(
    Platform.OS !== "web" && tabHost === null && tabRouteList.length > 0 && isNavigationFocused,
  );

  const tabBarProps = useMemo(() => {
    const routes = tabRouteList.map((route) => ({
      key: route.key,
      name: route.name,
      params: undefined,
    }));
    const navigate = (routeName: string) => {
      const nextIndex = tabRouteList.findIndex((route) => route.name === routeName);
      if (nextIndex !== -1) {
        setActiveIndex(nextIndex);
        onActiveTabChange?.(routeName);
      }
    };
    return createMaterialTopTabBarProps({
      activeIndex,
      getOptions: (route) => ({
        tabBarAccessibilityLabel:
          tabRouteList.find((entry) => entry.key === route.key)?.title ?? route.name,
        tabBarActiveTintColor: masteryColor,
        tabBarIcon: detailsTabBarIcon(route.name),
        tabBarIndicatorStyle: { backgroundColor: masteryColor, top: 0 },
        tabBarItemStyle: {
          flexDirection: "row",
          minHeight: 40,
          paddingHorizontal: 0,
          paddingVertical: 4,
        },
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: "400",
          margin: 0,
          textTransform: "none",
        },
        tabBarStyle: backgroundStyle,
        title: tabRouteList.find((entry) => entry.key === route.key)?.title ?? route.name,
      }),
      navigate,
      routes,
      stateKey: `${stateKeyPrefix}-${morpheme}-${morphemeHrefObject.href}`,
      withDispatchNavigation: true,
    });
  }, [
    activeIndex,
    backgroundStyle,
    masteryColor,
    morpheme,
    morphemeHrefObject,
    onActiveTabChange,
    tabRouteList,
    stateKeyPrefix,
    setActiveIndex,
  ]);

  // The surrounding detail route owns navigation. Embed Expo native tabs without
  // inserting a second router or changing its deep-link parameters. Inside a card the same
  // liquid glass bar floats over the card body; the card's binder only labels the Details view.
  if (Platform.OS === "ios" && tabRouteList.length > 0) {
    return (
      <View
        className="flex-1 bg-background"
        pointerEvents={tabHost && !tabHost.isInteractive ? "none" : "auto"}
      >
        <NativeTabsViewIOS
          focusedIndex={Math.min(activeIndex, tabRouteList.length - 1)}
          onTabChange={({ isNativeAction, isPrevented, provenance, selectedKey }) => {
            setNativeProvenance(provenance);
            if (!isNativeAction || isPrevented) {
              return;
            }
            // The native key is the tab's `name` (Expo's `screenKey`), not its `routeKey`.
            const nextIndex = tabRouteList.findIndex((route) => route.name === selectedKey);
            if (nextIndex !== -1) {
              setActiveIndex(nextIndex);
              if (!tabHost) {
                onActiveTabChange?.(tabRouteList[nextIndex].name);
              }
            }
          }}
          provenance={nativeProvenance}
          tabs={tabRouteList.map((route, index) => {
            const options: NativeTabOptions = {
              backgroundColor: backgroundStyle.backgroundColor,
              contentStyle: backgroundStyle,
              disableTransparentOnScrollEdge: true,
              selectedIconColor: masteryColor,
              selectedLabelStyle: { color: masteryColor },
              title: route.title,
            };
            appendIconOptionsIOS(options, infoTabIcons[route.name] ?? {});
            return {
              contentRenderer: () =>
                index === activeIndex ? (
                  <RouteBodyStateScope name={`info:${route.name}`}>
                    {route.render()}
                  </RouteBodyStateScope>
                ) : null,
              name: route.name,
              options,
              routeKey: route.key,
            };
          })}
          tintColor={masteryColor}
          unstable_nativeProps={{
            colorScheme: theme === "dark" ? "dark" : "light",
            ios:
              tabBarAccessory === undefined
                ? undefined
                : {
                    // A zero frame means there is no slot (hidden bar, or earlier than iOS 26).
                    onTabBarTrailingAccessoryLayout: ({ nativeEvent }) => {
                      const { height, width, x, y } = nativeEvent;
                      setAccessoryFrame(width > 0 ? { height, x, y } : null);
                    },
                    tabBarTrailingAccessoryDiameter: accessoryDiameter,
                    tabBarTrailingAccessoryEnabled: true,
                  },
          }}
        />
        {tabBarAccessory === undefined || accessoryFrame === null ? null : (
          <View
            className="absolute"
            style={{ left: accessoryFrame.x, top: accessoryFrame.y }}
            testID="morpheme-info-tab-bar-accessory"
          >
            {tabBarAccessory(accessoryFrame.height)}
          </View>
        )}
      </View>
    );
  }

  return (
    <View
      className="flex-1 bg-background"
      pointerEvents={tabHost && !tabHost.isInteractive ? "none" : "auto"}
    >
      <FrostedBackdropTarget
        active={isHostActive && (tabHost?.isInteractive ?? true)}
        className="min-h-0 flex-1"
      >
        <RouteBodyStateScope name={`info:${activeRoute?.name ?? "none"}`}>
          {activeRoute?.render() ?? null}
        </RouteBodyStateScope>
      </FrostedBackdropTarget>
      {Platform.OS === "android" ? (
        isHostActive ? (
          <MorphemeInfoFrostedTabs
            activeIndex={activeIndex}
            interactive={tabHost?.isInteractive ?? true}
            onSelect={(index) => {
              setActiveIndex(index);
              if (!tabHost) {
                onActiveTabChange?.(tabRouteList[index].name);
              }
            }}
            tabs={tabRouteList.map(({ name, title }) => ({ segment: name, title }))}
            testIDPrefix={tabHost ? "learn-card-info-section" : "morpheme-info-tab"}
            trailingAccessory={tabBarAccessory}
            trailingAccessoryDiameter={accessoryDiameter}
          />
        ) : null
      ) : tabHost ? (
        tabHost.isActive ? (
          <View className="h-10 shrink-0 flex-row border-t border-border">
            {tabRouteList.map((route, index) => {
              const isSelected = index === activeIndex;
              return (
                <Pressable
                  accessibilityLabel={route.title}
                  accessibilityRole="tab"
                  accessibilityState={{ disabled: !tabHost.isInteractive, selected: isSelected }}
                  className={`h-full min-w-0 flex-1 items-center justify-center border-l ${index > 0 ? "border-border" : "border-transparent"} ${isSelected ? "bg-surface" : "bg-muted"}`}
                  disabled={!tabHost.isInteractive}
                  key={route.name}
                  onPress={() => setActiveIndex(index)}
                  testID={`learn-card-info-section-${route.name}`}
                >
                  {detailsTabBarIcon(route.name)({
                    className: isSelected ? "text-foreground" : "text-muted-foreground/70",
                    focused: isSelected,
                  })}
                </Pressable>
              );
            })}
          </View>
        ) : null
      ) : (
        <DynamicIconsTabBar {...tabBarProps} />
      )}
    </View>
  );
}
